import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { CircleArrowLeftIcon, LogInIcon, TicketIcon } from "lucide-react";
import { useIsLoggedIn } from "@dynamic-labs/sdk-react-core";

import FullLogoIcon from "@/assets/svg/fullLogo.svg?react";
import UsdcIcon from "@/assets/svg/usdc-stroke.svg?react";
import { authActions, RootState } from "@/store";
import { useTicketBalance, useTranslate } from "@/hooks";
import { AvatarBadge } from "../main";
import { cn } from "@/helpers/tailwind";
import { formatBalance } from "@/helpers";
import { useBattleById } from "@/api/battleQueries";
import { BattleHeaderActions } from "@/pages/battles/components";
import isNil from "lodash.isnil";

interface MobileTopNavProps {
  className?: string;
}

export const MobileTopNav: React.FC<MobileTopNavProps> = ({ className }) => {
  const t = useTranslate();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isLoggedIn = useIsLoggedIn();
  const { balance } = useSelector((state: RootState) => state.wallet);
  const ticketBalance = useTicketBalance();
  const isSubpath = React.useMemo(
    () => (location.pathname.replace(/\/$/, "").match(/\//g) || []).length > 1,
    [location.pathname]
  );
  const { id } = useParams<{ id: string }>();
  const { data: battleData, refetch: refetchBattle } = useBattleById(
    `${id}`,
    false
  );

  const content = {
    loginBtn: (
      <button
        className="flex items-center"
        onClick={() => dispatch(authActions.toggleTriggerLogin())}>
        <div className="relative flex size-10 items-center justify-center">
          {/* Inner dashed ring */}
          <div className="absolute inset-0 -m-1 rounded-full border border-dashed border-slate-500" />

          {/* Sign in icon in center */}
          <div className="flex size-9 items-center justify-center rounded-full bg-slate-200 text-slate-900">
            <LogInIcon className="size-5" />
          </div>
        </div>
      </button>
    ),
  };

  const renderNavActions = () => {
    const isBattlePage = location.pathname.includes("/battles/") && !isNil(id);

    if (isBattlePage) {
      return (
        <BattleHeaderActions
          isMobileTopNav
          battle={battleData}
          loginBtn={content.loginBtn}
          refetchBattle={refetchBattle}
        />
      );
    }

    if (!isLoggedIn) {
      return content.loginBtn;
    }

    return (
      <div className="flex items-center gap-3">
        <div className="flex flex-col gap-1">
          {/* USDC balance */}
          <div className="flex items-center justify-end gap-1">
            <UsdcIcon className="size-4" />
            <span className="whitespace-nowrap text-xs font-semibold xl:text-sm">
              {formatBalance(Number(balance))}
            </span>
          </div>
          {/* Ticket balance */}
          <div className="flex items-center justify-end gap-1 text-slate-400">
            <span className="whitespace-nowrap text-xs font-semibold xl:text-sm">
              {"$ "}
              {formatBalance(Number(ticketBalance))}
            </span>
            <TicketIcon className="size-3" />
          </div>
        </div>
        <button
          className="flex items-center"
          onClick={() => navigate(`/profile?profileTab=Battles`)}>
          <AvatarBadge className="size-10" ringClassName="border-slate-500" />
        </button>
      </div>
    );
  };

  return (
    <div
      className={cn(
        "z-40 flex w-full items-center justify-between gap-2 bg-background py-6 pl-4 pr-8",
        className
      )}>
      {isSubpath ? (
        <button
          className="flex items-center gap-2"
          onClick={() => navigate(-1)}>
          <CircleArrowLeftIcon className="size-8 stroke-1" />
          <span className="font-phonk text-2xl">{t("common.nav.back")}</span>
        </button>
      ) : (
        <FullLogoIcon className="h-8 text-lightblue" />
      )}

      {renderNavActions()}
    </div>
  );
};
